/**
 * Image carousel.
 *
 * Progressive enhancement over the rendered track: without JS the track is a
 * horizontally scrollable strip, which is already usable. This adds paging,
 * arrows, dots, keyboard support and optional autoplay. No dependencies.
 *
 * Slides-per-view comes from the CSS var --bl-per-view on the track, so the
 * layout stays a single source of truth.
 */
window.Blockline = window.Blockline || {};

window.Blockline.carousel = ( function () {
	'use strict';

	/**
	 * Wire up one carousel instance.
	 *
	 * @param {HTMLElement} root The .bl-carousel section.
	 */
	function setup( root ) {
		var track = root.querySelector( '.bl-carousel__track' );
		var slides = track ? Array.prototype.slice.call( track.children ) : [];

		if ( ! track || slides.length === 0 ) {
			return;
		}

		var prevBtn = root.querySelector( '[data-carousel-prev]' );
		var nextBtn = root.querySelector( '[data-carousel-next]' );
		var dotsWrap = root.querySelector( '[data-carousel-dots]' );

		var loop = root.dataset.loop === 'true';
		var autoplay = root.dataset.autoplay === 'true';
		var interval = parseInt( root.dataset.interval, 10 ) || 5000;

		var index = 0;
		var timer = null;

		function perView() {
			// Measure what's actually visible, so it matches the responsive CSS.
			var width = slides[ 0 ].getBoundingClientRect().width;
			return width > 0 ? Math.max( 1, Math.round( track.clientWidth / width ) ) : 1;
		}

		function maxIndex() {
			return Math.max( 0, slides.length - perView() );
		}

		function goTo( target ) {
			var max = maxIndex();

			if ( target < 0 ) {
				target = loop ? max : 0;
			} else if ( target > max ) {
				target = loop ? 0 : max;
			}

			index = target;
			track.scrollTo( { left: slides[ index ].offsetLeft - track.offsetLeft, behavior: 'smooth' } );
			updateDots();
		}

		function next() {
			goTo( index + 1 );
		}

		function prev() {
			goTo( index - 1 );
		}

		/* --- Dots ---------------------------------------------------------- */

		function buildDots() {
			if ( ! dotsWrap ) {
				return;
			}

			dotsWrap.innerHTML = '';

			var pages = maxIndex() + 1;

			for ( var i = 0; i < pages; i++ ) {
				( function ( page ) {
					var dot = document.createElement( 'button' );
					dot.type = 'button';
					dot.className = 'bl-carousel__dot';
					dot.setAttribute( 'aria-label', 'Go to slide ' + ( page + 1 ) );
					dot.addEventListener( 'click', function () {
						goTo( page );
						restart();
					} );
					dotsWrap.appendChild( dot );
				} )( i );
			}

			updateDots();
		}

		function updateDots() {
			if ( ! dotsWrap ) {
				return;
			}

			Array.prototype.forEach.call( dotsWrap.children, function ( dot, i ) {
				var active = i === index;
				dot.setAttribute( 'aria-current', active ? 'true' : 'false' );
				dot.classList.toggle( 'is-active', active );
			} );
		}

		/* --- Autoplay ------------------------------------------------------ */

		function start() {
			if ( ! autoplay || window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
				return;
			}
			timer = window.setInterval( next, interval );
		}

		function stop() {
			if ( timer ) {
				window.clearInterval( timer );
				timer = null;
			}
		}

		function restart() {
			stop();
			start();
		}

		/* --- Events -------------------------------------------------------- */

		if ( nextBtn ) {
			nextBtn.addEventListener( 'click', function () {
				next();
				restart();
			} );
		}

		if ( prevBtn ) {
			prevBtn.addEventListener( 'click', function () {
				prev();
				restart();
			} );
		}

		root.addEventListener( 'keydown', function ( event ) {
			if ( event.key === 'ArrowRight' ) {
				next();
				restart();
			} else if ( event.key === 'ArrowLeft' ) {
				prev();
				restart();
			}
		} );

		root.addEventListener( 'mouseenter', stop );
		root.addEventListener( 'mouseleave', start );
		root.addEventListener( 'focusin', stop );
		root.addEventListener( 'focusout', start );

		// Keep the active dot in sync if the user scrolls the track directly.
		var scrollTick;
		track.addEventListener( 'scroll', function () {
			window.clearTimeout( scrollTick );
			scrollTick = window.setTimeout( function () {
				var nearest = 0;
				var min = Infinity;

				slides.forEach( function ( slide, i ) {
					var distance = Math.abs( slide.offsetLeft - track.offsetLeft - track.scrollLeft );
					if ( distance < min ) {
						min = distance;
						nearest = i;
					}
				} );

				index = Math.min( nearest, maxIndex() );
				updateDots();
			}, 100 );
		} );

		window.addEventListener( 'resize', buildDots );

		buildDots();
		start();
	}

	function init() {
		document.querySelectorAll( '.bl-carousel' ).forEach( setup );
	}

	return { init: init };
} )();
